import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '@/components/app/button';
import {Input} from '@/components/ui/input';
import {Icon} from '@/app/components/Icon';
import {cn} from '@/lib/utils';
import {useLazyGetBiotwinsQuery} from '../../../app/services/biotwinApi';
import {toastError, toastInfo} from '../../../app/toast';
import {MatchMode} from '../../../app/types';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {selectTenantId} from '../../auth/authSlice';

/** Header search that sits as a round icon and widens into an input on hover or focus. */
export const GoToBiotwin = () => {
  const tenantId = useAppSelector(selectTenantId);
  const [getBiotwins, {isFetching}] = useLazyGetBiotwinsQuery();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const timeoutId = useRef<number | undefined>(undefined);
  const query = searchQuery.trim();
  useEffect(() => () => window.clearTimeout(timeoutId.current), []);
  // A short delay keeps the field from flickering when the pointer only passes over it.
  const setExpandedLater = (next: boolean) => {
    window.clearTimeout(timeoutId.current);
    timeoutId.current = window.setTimeout(() => {
      setExpanded(next);
      if (!next) setSearchQuery('');
    }, 100);
  };
  const collapse = () => {
    window.clearTimeout(timeoutId.current);
    setExpanded(false);
    setSearchQuery('');
  };
  return (
    <form
      role="search"
      aria-label="Go to Biotwin"
      className="relative"
      noValidate
      onMouseEnter={() => setExpandedLater(true)}
      onMouseLeave={() => {
        if (!isFetching && document.activeElement !== input.current) setExpandedLater(false);
      }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (!query || isFetching) return;
        try {
          const result = await getBiotwins({name: query, nameMatchMode: MatchMode.Exact, tenantId}).unwrap();
          if (result.items.length) {
            collapse();
            input.current?.blur();
            navigate(`/biotwins/${result.items[0].id}`);
          } else toastInfo('No biotwin found');
        } catch (error) {
          toastError(error);
        }
      }}
    >
      <Input
        ref={input}
        aria-label="Biotwin name"
        placeholder="Go to Biotwin"
        readOnly={isFetching}
        value={searchQuery}
        className={cn(
          'h-10 rounded-full text-sm transition-[width,background-color,border-color] duration-200 md:text-sm',
          expanded
            ? 'w-48 border-input bg-card pr-10 pl-4 text-foreground'
            : 'w-10 cursor-pointer border-navigation-accent bg-transparent px-0 text-transparent placeholder:text-transparent',
        )}
        onChange={(event) => setSearchQuery(event.target.value)}
        onFocus={() => {
          window.clearTimeout(timeoutId.current);
          setExpanded(true);
        }}
        onBlur={() => setExpandedLater(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            collapse();
            input.current?.blur();
          }
        }}
      />
      <Button
        type={query ? 'submit' : 'button'}
        variant={expanded ? 'ghost' : 'navigation'}
        size="icon"
        className={cn('absolute top-0.5 right-0.5 rounded-full', expanded && 'text-foreground')}
        aria-label={query ? 'Find biotwin' : 'Go to Biotwin'}
        // Keeps the input focused, so clicking the button does not collapse the field first.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (!query) input.current?.focus();
        }}
        disabled={isFetching}
        startIcon={<Icon icon={isFetching ? 'spinner' : query ? 'arrow-right' : 'search'} />}
      />
    </form>
  );
};
